import React, { useState, useEffect, useCallback } from 'react';
import { metricsTemplatesService, sitesService, clientsService } from '../services';
import { getMetricTypeOptions } from '../utils/metricFormatting';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Trash2, Edit3, Archive, GripVertical, Link2, Unlink, Loader2, Search
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getApiError } from '@/lib/utils';

const METRIC_TYPE_OPTIONS = getMetricTypeOptions();

export default function MetricsTemplatesSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [archiveId, setArchiveId] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await metricsTemplatesService.list(false);
      setTemplates(data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openEditor = (template = null) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleArchive = async () => {
    if (!archiveId) return;
    try {
      await metricsTemplatesService.archive(archiveId);
      toast.success('Template archived');
      setArchiveId(null);
      loadTemplates();
    } catch { toast.error('Failed to archive'); }
  };

  if (loading) return <div className="flex items-center gap-2 py-8 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading templates...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Define custom metrics captured during Beverage Stock Audit Reports</p>
        <Button size="sm" onClick={() => openEditor()} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-1" /> New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-500">No templates yet. Create one to get started.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <Card key={t.id} data-testid={`template-card-${t.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{t.title}</CardTitle>
                    {t.description && <CardDescription>{t.description}</CardDescription>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditor(t)} data-testid={`edit-template-${t.id}`}>
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowAssignDialog(t.id)} data-testid={`assign-template-${t.id}`}>
                      <Link2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setArchiveId(t.id)} className="text-red-500">
                      <Archive className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xs text-muted-foreground mb-2">
                  {t.lines.filter(l => l.is_active).length} metric(s) &middot; {t.is_active ? 'Active' : 'Inactive'}
                </div>
                <div className="space-y-1 mb-3">
                  {t.lines.filter(l => l.is_active).map(l => (
                    <div key={l.id} className="flex items-center gap-2 text-sm pl-2 border-l-2 border-slate-200">
                      <GripVertical className="h-3 w-3 text-slate-300" />
                      <span>{l.metric_label}</span>
                      <Badge variant="outline" className="text-[10px] px-1 py-0">{l.metric_type}</Badge>
                      {l.note_enabled && <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">Note</span>}
                    </div>
                  ))}
                </div>
                {t.assignments.length > 0 && (
                  <div className="border-t pt-2">
                    <span className="text-xs font-medium text-muted-foreground">Assigned to:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {t.assignments.map(a => (
                        <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                          {a.entity_name || `${a.assignment_type} #${a.entity_id}`}
                          <button onClick={async () => { await metricsTemplatesService.removeAssignment(a.id); toast.success('Removed'); loadTemplates(); }}
                            className="hover:text-red-600" data-testid={`unassign-${a.id}`}>
                            <Unlink className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showEditor && (
        <TemplateEditor
          template={editingTemplate}
          onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
          onSaved={() => { setShowEditor(false); setEditingTemplate(null); loadTemplates(); }}
        />
      )}

      {showAssignDialog && (
        <AssignDialog
          templateId={showAssignDialog}
          existingAssignments={templates.find(t => t.id === showAssignDialog)?.assignments || []}
          onClose={() => setShowAssignDialog(null)}
          onAssigned={() => { setShowAssignDialog(null); loadTemplates(); }}
        />
      )}

      <AlertDialog open={!!archiveId} onOpenChange={(o) => !o && setArchiveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this template?</AlertDialogTitle>
            <AlertDialogDescription>Archived templates won't be used for new reports. Existing reports are unaffected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} className="bg-red-600 hover:bg-red-700">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ── Template Editor ──────────────────────────────────────────────────────

function TemplateEditor({ template, onClose, onSaved }) {
  const [title, setTitle] = useState(template?.title || '');
  const [description, setDescription] = useState(template?.description || '');
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [lines, setLines] = useState(
    template?.lines?.map((l, i) => ({ ...l, _key: i })) || [{ _key: 0, metric_label: '', metric_type: 'PERCENTAGE', note_enabled: false, display_order: 1, is_active: true }]
  );
  const [saving, setSaving] = useState(false);

  const addLine = () => {
    setLines([...lines, { _key: Date.now(), metric_label: '', metric_type: 'PERCENTAGE', note_enabled: false, display_order: lines.length + 1, is_active: true }]);
  };

  const updateLine = (key, field, value) => {
    setLines(lines.map(l => l._key === key ? { ...l, [field]: value } : l));
  };

  const removeLine = (key) => {
    setLines(lines.filter(l => l._key !== key));
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (lines.filter(l => l.is_active && l.metric_label.trim()).length === 0) { toast.error('At least one metric line is required'); return; }

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        is_active: isActive,
        lines: lines.filter(l => l.metric_label.trim()).map((l, i) => ({
          metric_label: l.metric_label.trim(),
          metric_type: l.metric_type,
          note_enabled: l.note_enabled,
          display_order: i + 1,
          is_active: l.is_active,
        })),
      };

      if (template?.id) {
        await metricsTemplatesService.update(template.id, payload);
        toast.success('Template updated');
      } else {
        await metricsTemplatesService.create(payload);
        toast.success('Template created');
      }
      onSaved();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'New Metrics Template'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Template Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Beverage KPIs" data-testid="template-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe when this template should be used" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>Active</Label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold">Metric Lines</Label>
              <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
                <Plus className="h-3 w-3 mr-1" /> Add Line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l._key} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                  <span className="text-xs text-muted-foreground w-6 text-center">{i + 1}</span>
                  <Input
                    className="flex-1"
                    value={l.metric_label}
                    onChange={e => updateLine(l._key, 'metric_label', e.target.value)}
                    placeholder="Metric label"
                    data-testid={`line-label-${i}`}
                  />
                  <Select value={l.metric_type} onValueChange={v => updateLine(l._key, 'metric_type', v)}>
                    <SelectTrigger className="w-[130px]" data-testid={`line-type-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METRIC_TYPE_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={l.note_enabled} onCheckedChange={v => updateLine(l._key, 'note_enabled', v)} />
                    <span className="text-xs text-muted-foreground">Note</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={l.is_active} onCheckedChange={v => updateLine(l._key, 'is_active', v)} />
                    <span className="text-xs text-muted-foreground">Active</span>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => removeLine(l._key)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {template ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Assign Dialog (Unified list: clients + sites, no type dropdown) ──────

function AssignDialog({ templateId, existingAssignments, onClose, onAssigned }) {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set()); // "CLIENT:id" or "SITE:id"
  const [saving, setSaving] = useState(false);

  // Build set of already-assigned keys like "CLIENT:24" or "SITE:102"
  const alreadyAssigned = new Set(
    existingAssignments.map(a => `${a.assignment_type}:${a.entity_id}`)
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [clientsData, sitesData] = await Promise.all([
          clientsService.list({ is_active: true, limit: 1000 }),
          sitesService.list({ limit: 1000, is_active: true }),
        ]);
        if (!cancelled) {
          const cItems = Array.isArray(clientsData) ? clientsData : clientsData?.items || [];
          const sItems = Array.isArray(sitesData) ? sitesData : sitesData?.items || [];
          setClients(cItems.map(c => ({ id: c.id, name: c.name })));
          setSites(sItems.map(s => ({ id: s.id, name: s.name })));
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Build unified list with type tags
  const allEntities = [
    ...clients.map(c => ({ key: `CLIENT:${c.id}`, id: c.id, name: c.name, type: 'CLIENT' })),
    ...sites.map(s => ({ key: `SITE:${s.id}`, id: s.id, name: s.name, type: 'SITE' })),
  ];

  const filtered = allEntities.filter(e => {
    if (alreadyAssigned.has(e.key)) return false;
    if (!search.trim()) return true;
    return e.name.toLowerCase().includes(search.toLowerCase());
  });

  const filteredClients = filtered.filter(e => e.type === 'CLIENT');
  const filteredSites = filtered.filter(e => e.type === 'SITE');

  const toggleEntity = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleAssign = async () => {
    if (selected.size === 0) { toast.error('Select at least one item'); return; }
    setSaving(true);
    try {
      // Split selections by type
      const clientIds = [];
      const siteIds = [];
      for (const key of selected) {
        const [type, id] = key.split(':');
        if (type === 'CLIENT') clientIds.push(parseInt(id));
        else if (type === 'SITE') siteIds.push(parseInt(id));
      }

      const results = [];
      if (clientIds.length > 0) {
        results.push(await metricsTemplatesService.batchAssign(templateId, 'CLIENT', clientIds));
      }
      if (siteIds.length > 0) {
        results.push(await metricsTemplatesService.batchAssign(templateId, 'SITE', siteIds));
      }

      const totalCreated = results.reduce((sum, r) => sum + (r.created_count || 0), 0);
      toast.success(`Assigned to ${totalCreated} client(s)/site(s)`);
      onAssigned();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to assign'));
    } finally { setSaving(false); }
  };

  const renderSection = (title, items) => {
    if (items.length === 0) return null;
    return (
      <>
        <div className="px-3 py-1.5 bg-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wide sticky top-0">
          {title} ({items.length})
        </div>
        {items.map(e => (
          <label
            key={e.key}
            className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
            data-testid={`assign-entity-${e.key}`}
          >
            <Checkbox
              checked={selected.has(e.key)}
              onCheckedChange={() => toggleEntity(e.key)}
            />
            <span className="text-sm">{e.name}</span>
          </label>
        ))}
      </>
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Assign Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 min-h-0 flex flex-col">
          <p className="text-xs text-muted-foreground">Select any combination of clients and sites. Site-level assignments take precedence over client-level.</p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search clients and sites..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="assign-search-input"
            />
          </div>
          <div className="border rounded-md overflow-y-auto flex-1 min-h-[200px] max-h-[350px]">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading...
              </div>
            ) : (filteredClients.length === 0 && filteredSites.length === 0) ? (
              <div className="py-8 text-center text-muted-foreground text-sm">
                {search ? 'No results matching your search' : 'All clients and sites are already assigned'}
              </div>
            ) : (
              <div>
                {renderSection('Clients', filteredClients)}
                {renderSection('Sites', filteredSites)}
              </div>
            )}
          </div>
          {selected.size > 0 && (
            <div className="text-sm text-muted-foreground">
              {selected.size} selected
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAssign} disabled={saving || selected.size === 0} data-testid="confirm-assign-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Assign ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
